import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, setSessionToken, clearSessionToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, roles: [], member: null, loading: true });

  // On mount — try to restore session (cookie or sessionStorage token)
  const refresh = useCallback(async () => {
    try {
      const data = await api.get("/auth/me");
      setState({ user: data.user, roles: data.roles, member: data.member, loading: false });
    } catch {
      clearSessionToken();
      setState({ user: null, roles: [], member: null, loading: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for 401 events dispatched by api.js → force sign-out
  useEffect(() => {
    const onExpired = () => {
      setState((prev) => (prev.user ? { user: null, roles: [], member: null, loading: false } : prev));
    };
    window.addEventListener("lv-auth-expired", onExpired);
    return () => window.removeEventListener("lv-auth-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const data = await api.post("/auth/login", { email, password });
    // Store session token for Bearer fallback
    if (data.session_token) {
      setSessionToken(data.session_token);
    }
    setState({ user: data.user, roles: data.roles, member: data.member, loading: false });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearSessionToken();
    setState({ user: null, roles: [], member: null, loading: false });
  };

  const setAuthData = (data) => {
    if (data.session_token) {
      setSessionToken(data.session_token);
    }
    setState({ user: data.user, roles: data.roles, member: data.member, loading: false });
  };

  const isAdmin = state.roles.some(
    (r) => r.role === "GLOBAL_ADMIN" || r.role === "CHAPTER_ADMIN"
  );
  const isMember = state.member !== null;

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh, setAuthData, isAdmin, isMember }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
